import { i18n } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Container, Graphics, Sprite, Text, TextStyle, Texture, TilingSprite } from 'pixi.js';

import { Shared } from '../App';
import { RaceType } from '../game/systems/RaceSystem';
import Curve from '../utils/Curve';
import { StrUtils } from '../utils/StrUtils';
import { CharacterIcon } from './CharacterIcon';
import { Widget } from './Widget';

const defaultData = {
    race: 0,
    position: 0,
    players: 6,
    time: 0,
    raceType: 'standard' as RaceType,
    step: 0,
    nemesisCount: 0
};

export type RacePosterData = typeof defaultData;

export class RacePoster extends Widget
{
    public content: Container;
    public data: RacePosterData;
    public bg: Graphics;
    public border: Sprite;
    public checked: TilingSprite;
    public checkedMask: Graphics;
    public expander: Container;
    public nemesisBrandLogo: Sprite;
    public track: Sprite;
    public index = -1;
    private _baseWidth = 338;
    private _baseHeight = 226;
    private trackTexturesStandard: Texture[];
    private trackTexturesNemesis: Texture[];
    private positionMedalTextures: Texture[];
    private positionLocaleKeys: string[];
    private animated = false;

    public title: Sprite;
    public position: Sprite;
    public time: Sprite;
    public stopwatchIcon: Sprite;
    public positionMedal: Sprite;
    private textStyleTitle: TextStyle;
    private textStylePosition: TextStyle;

    private characterIcon?: CharacterIcon;

    // Experimental - Cached text to smooth scroller updates
    private textCache: Record<string, Text>;

    constructor(animated = false)
    {
        super();
        this.animated = animated;
        this.data = { ...defaultData };
        this.textCache = {};

        this.content = new Container();
        this.view.addChild(this.content);

        this.textStyleTitle = {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'center',
            fontSize: 50,
            stroke: 0x333333,
            strokeThickness: 8,
            lineJoin: 'round',
        } as TextStyle;

        this.textStylePosition = {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'center',
            fontSize: 22,
            stroke: 0x333333,
            strokeThickness: 6,
            lineJoin: 'round',
            lineHeight: 22,
        } as TextStyle;

        this.bg = new Graphics();
        this.bg.beginFill(0x254661);
        this.bg.drawRoundedRect(-this._baseWidth / 2, -this._baseHeight / 2, this._baseWidth, this._baseHeight, 40);
        this.content.addChild(this.bg);

        this.checked = new TilingSprite(Shared.assets.getTexture('raceposter-checkbg'));
        this.checked.anchor.set(0.5);
        this.checked.width = this._baseWidth * 2;
        this.checked.height = this._baseHeight * 2;
        this.content.addChild(this.checked);

        this.checkedMask = new Graphics();
        this.checkedMask.beginFill(0xFF0000, 0.5);
        this.checkedMask.drawRoundedRect(-this._baseWidth / 2, -this._baseHeight / 2, this._baseWidth, this._baseHeight, 40);
        this.content.addChild(this.checkedMask);
        this.checked.mask = this.checkedMask;

        this.expander = new Container();
        this.content.addChild(this.expander);

        this.nemesisBrandLogo = new Sprite(Shared.assets.getTexture('logo-awog'));
        this.nemesisBrandLogo.anchor.set(0.5);
        this.nemesisBrandLogo.mask = this.checkedMask;
        this.expander.addChild(this.nemesisBrandLogo);

        this.track = new Sprite();
        this.track.anchor.set(0.5);
        this.track.scale.set(1.05)
        this.expander.addChild(this.track);
        this.trackTexturesStandard = [
            Shared.assets.getTexture('raceposter-standardrace1'),
            Shared.assets.getTexture('raceposter-standardrace2'),
            Shared.assets.getTexture('raceposter-standardrace3'),
        ];

        this.trackTexturesNemesis = [
            Shared.assets.getTexture('raceposter-nemesisrace'),
        ];
        
        this.positionMedalTextures = [
            Shared.assets.getTexture('icon_gold'),
            Shared.assets.getTexture('icon_silver'),
            Shared.assets.getTexture('icon_bronze')
        ];
        
        this.positionLocaleKeys = [
            "preRaceScreen_first",
            "preRaceScreen_second",
            "preRaceScreen_third"
        ]

        this.title = new Sprite();
        this.title.anchor.set(0.5);
        this.expander.addChild(this.title);
        
        this.positionMedal = new Sprite(Shared.assets.getTexture('icon_gold'));
        this.positionMedal.anchor.set(0.5);
        this.positionMedal.x = -35;
        this.expander.addChild(this.positionMedal);
        
        this.position = new Sprite();
        this.position.anchor.set(0.5);
        this.position.x = 35;
        this.expander.addChild(this.position);
        
        this.stopwatchIcon = new Sprite(Shared.assets.getTexture('icon_stopwatch'));
        this.stopwatchIcon.anchor.set(0.5);
        this.stopwatchIcon.x = -35;
        this.expander.addChild(this.stopwatchIcon);
        
        this.time = new Sprite();
        this.time.anchor.set(0.5);
        this.time.x = 35;
        this.expander.addChild(this.time);
        
        this.border = new Sprite(Shared.assets.getTexture('raceposter-border'));
        this.content.addChild(this.border);
        this.border.anchor.set(0.5);
    }

    public setup(data: RacePosterData): void
    {
        this.data = data;
        this.refresh();
    }

    public refresh(): void
    {
        // Position and adjust everything based on current _step value.
        // Step 1 should be expanded, and 0 collapsed
        const step = this.data.step;
        const inverseStep = 1 - step;
        const w = this._baseWidth - (this._baseWidth * (0.4 * inverseStep));
        const h = this._baseHeight - (this._baseHeight * (0.4 * inverseStep));
        const nemesis = this.data.raceType === 'nemesis';

        // --- UPDATE POSTER SIZE ---
        this.view.scale.set(w / this._baseWidth);
        this.expander.scale.set(1 + (Curve.sineIn(inverseStep) * 0.5));
        this.border.tint = this.data.position ? 0xfae42e : 0xFFFFFF;
        
        this.content.scale.set(1 - (step * 0.15));
        this.content.y = (step * 35);

        // --- UPDATE CHECKED BG ---
        this.checked.alpha = step;
        this.checked.renderable = this.checked.alpha > 0.1;

        // --- UPDATE TITLE ---
        this.title.y = this.data.position ? (-h / 2) + 30 + (60 * step) : -60 * step;
        // eslint-disable-next-line max-len
        if(nemesis){
            this.title.texture = this.getCachedText(i18n.translate('game_race_nemesis', {num: this.data.nemesisCount}), this.textStyleTitle).texture;
        }
        else{
            this.title.texture = this.getCachedText(i18n.translate('game_race', {num: this.data.race - this.data.nemesisCount}), this.textStyleTitle).texture;
        }
        
        this.title.scale.set(0.75 + (step * 0.25));
        this.title.scale.set(Math.min(this.title.scale.x, (w - 25 - (inverseStep * 25)) / this.title.width));

        this.nemesisBrandLogo.alpha = ((step * 0.5));
        this.nemesisBrandLogo.scale.set(0.5 + (step * 1.5));
        this.nemesisBrandLogo.angle = step * -30;
        this.nemesisBrandLogo.renderable = this.nemesisBrandLogo.alpha > 0.05 && nemesis;

        if(this.nemesisBrandLogo.renderable){
            this.nemesisBrandLogo.texture = Shared.assets.getTexture(`logo-${Shared.game.race.getNemesisBrand(this.data.race)}`);
        }

        // --- UPDATE TRACK SPRITE ---
        this.track.alpha = (step * 2.5) - 1.5;
        this.track.renderable = this.track.alpha > 0.05 && !this.data.position;
        if (this.track.renderable)
        {
            const textures = nemesis ? this.trackTexturesNemesis : this.trackTexturesStandard;

            this.track.y = 200 * Curve.sineIn(inverseStep);
            this.track.texture = textures[this.data.race % textures.length];
        }

        // --- UPDATE POSITION LABEL ---
        this.position.renderable = !!this.data.position;
        this.positionMedal.renderable = this.position.renderable;
        
        this.time.renderable = !!this.data.position;
        this.stopwatchIcon.renderable = this.time.renderable;
        
        if (this.position.renderable)
        {
            if (!nemesis)
            {
                const positionTime = StrUtils.formatRaceTime(this.data.time);

                this.position.texture = this.getCachedText(i18n.translate(this.positionLocaleKeys[this.data.position - 1]), this.textStylePosition).texture;
                this.position.y = (h / 2) - 60 - (10 * Curve.sineIn(step));
                
                this.time.texture = this.getCachedText(positionTime, this.textStylePosition).texture;
                this.time.y = (h / 2) - 20 - (10 * Curve.sineIn(step));
            }
            else
            {
                const positionValue = this.data.position === 1
                    ? i18n.translate('game_won')
                    : i18n.translate('game_lost');
                const positionTime = StrUtils.formatRaceTime(this.data.time);

                this.position.texture = this.getCachedText(positionValue, this.textStylePosition).texture;
                this.position.y = (h / 2) - 60 - (10 * Curve.sineIn(step));
                
                this.time.texture = this.getCachedText(positionTime, this.textStylePosition).texture;
                this.time.y = (h / 2) - 20 - (10 * Curve.sineIn(step));
            }
            
            this.position.scale.set(1);
            this.position.scale.set(Math.min(1, 90 / this.position.width));
            
            this.time.scale.set(1);
            this.time.scale.set(Math.min(1, 90 / this.time.width));
            
            this.positionMedal.texture = this.positionMedalTextures[Math.min(this.data.position - 1, this.positionMedalTextures.length - 1)];
        }
        this.positionMedal.y = this.position.y;
        this.stopwatchIcon.y = this.time.y;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public update(delta = 1): void
    {
        if (!this.view.visible) return;
        this.checked.x -= 0.9;
        this.checked.y -= 0.9;
        this.checked.x %= 42;
        this.checked.y %= 42;
    }

    /**
     * Get a cached text display obj for given string and style
     */
    private getCachedText(text: string, style: TextStyle): Text
    {
        if (!this.textCache[text])
        {
            this.textCache[text] = new Text(text, style);
            this.textCache[text].updateText(true);
        }

        return this.textCache[text];
    }

    protected async onOpen(): Promise<void>
    {
        if (!this.animated) return;
        gsap.killTweensOf(this.content);
        this.content.y = -300;
        await gsap.to(this.content, { y: 0, duration: 0.4, ease: 'back.out', delay: 0.4 });
    }

    protected async onClose(): Promise<void>
    {
        if (!this.animated) return;
        gsap.killTweensOf(this.content);
        await gsap.to(this.content, { y: -300, duration: 0.3, ease: 'back.in' });
    }

    public setCharacterIcon(id: string): void
    {
        if (!id)
        {
            this.clearCharacterIcon();
        }
        else
        {
            if (!this.characterIcon) this.characterIcon = new CharacterIcon();
            this.characterIcon.setCharacter(id, 0.75);
            this.characterIcon.renderable = true;
            this.characterIcon.x = 150;
            this.characterIcon.y = 20;
            this.content.addChild(this.characterIcon);
        }
    }

    public clearCharacterIcon(): void
    {
        if (!this.characterIcon) return;
        this.characterIcon.renderable = false;
    }
}
